/* eslint-disable no-use-before-define */
import { Button, Divider, Menu, MenuItem } from '@material-ui/core';
import React, { useCallback, useMemo, useState } from 'react';
import { Country, getCountryCallingCode } from 'react-phone-number-input/input';

export type Option = {
  value: string;
  label: string;
};

type CustomCountrySelectProps = {
  value: string;
  onChange: (value: Country) => void;
  options: Option[];
};

export const PREFERRED_COUNTRIES = ['FR', 'BE', 'CH', 'CA'];

const flagWrapperStyle: React.CSSProperties = {
  height: '18px',
  width: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const FlagImg = ({ countryCode }: { countryCode: string }) => (
  <img
    src={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`}
    alt={countryCode}
    width={28}
    height={18}
    style={{ display: 'block', objectFit: 'cover' }}
  />
);

export function CustomCountrySelectWithFlags({
  value,
  onChange,
  options,
}: CustomCountrySelectProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleOptionClick = useCallback(
    (newValue: string) => {
      onChange(newValue as Country);
      handleClose();
    },
    [onChange, handleClose]
  );

  const countryOptions = useMemo(
    () =>
      options.map((option, index) => {
        const isPreferred = PREFERRED_COUNTRIES.includes(option.value);
        const previousOption = index > 0 ? options[index - 1] : null;
        const showDivider =
          previousOption && PREFERRED_COUNTRIES.includes(previousOption.value) && !isPreferred;

        return [
          showDivider && <Divider key="divider" />,
          <MenuItem
            key={option.value || 'international'}
            onClick={() => handleOptionClick(option.value)}
            style={{ minWidth: 280, backgroundColor: isPreferred ? '#f0f0f0' : 'transparent' }}>
            {option.value && (
              <div style={flagWrapperStyle}>
                <FlagImg countryCode={option.value} />
              </div>
            )}
            <span style={{ marginLeft: '10px' }}>{option.label}</span>
            {option.value && (
              <span style={{ color: '#888', marginLeft: '10px' }}>
                +{getCountryCallingCode(option.value as any)}
              </span>
            )}
          </MenuItem>,
        ];
      }),
    [options, handleOptionClick]
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Button
        aria-controls="country-menu"
        aria-haspopup="true"
        onClick={handleClick}
        style={{ padding: '5px 8px 8px 8px', minWidth: 'auto', height: '100%' }}>
        {value && (
          <div style={flagWrapperStyle}>
            <FlagImg countryCode={value} />
          </div>
        )}
        <span style={{ marginLeft: '0.3em', fontSize: '0.8em', color: '#888' }}>▼</span>
      </Button>

      <Menu
        id="country-menu"
        anchorEl={anchorEl}
        keepMounted={true}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: 250,
          },
        }}>
        {countryOptions}
      </Menu>
    </div>
  );
}
